import type { AgentTemplate, AgentTemplateGrant } from '@reach/shared-types/agent-templates';
import {
  type AgentTemplateKind,
  SYSTEM_TOOL_NAMES_BY_TEMPLATE_KIND,
  type SystemToolName,
} from '@reach/shared-types/agents';
import type { GrantableTool } from './agent-templates-api';
import { EDITOR_COPY, systemToolLabel } from './agent-templates-copy';

export interface GrantedTool {
  grant: AgentTemplateGrant;
  label: string;
  builtIn: boolean;
  /** Null when the tool catalogue could not be loaded. */
  available: boolean | null;
}

const isSystemToolOfKind = (kind: AgentTemplateKind, toolName: string): toolName is SystemToolName => {
  const systemToolNames: readonly string[] = SYSTEM_TOOL_NAMES_BY_TEMPLATE_KIND[kind];
  return systemToolNames.includes(toolName);
};

export function describeGrants(
  template: AgentTemplate,
  tools: GrantableTool[] | undefined,
): GrantedTool[] {
  const toolsById = new Map(tools?.map(tool => [tool.id, tool]));
  return template.grants.map(grant => {
    const systemToolName = grant.systemToolName;
    if (systemToolName !== null) {
      if (isSystemToolOfKind(template.kind, systemToolName)) {
        return { grant, label: systemToolLabel(systemToolName), builtIn: true, available: true };
      }
      return { grant, label: systemToolName, builtIn: true, available: false };
    }
    const tool = grant.toolDefinitionId === null ? undefined : toolsById.get(grant.toolDefinitionId);
    if (tool) {
      return { grant, label: tool.display_name, builtIn: false, available: true };
    }
    return {
      grant,
      label: grant.toolDisplayName ?? grant.toolName ?? EDITOR_COPY.unknownTool,
      builtIn: false,
      available: tools === undefined ? null : false,
    };
  });
}

const displayRank = (tool: GrantedTool): number => {
  if (tool.available === false) return 2;
  return tool.builtIn ? 0 : 1;
};

/** Built-in tools first, then the partner's tools, then anything unavailable; alphabetical within each. */
export const sortGrantedTools = (tools: GrantedTool[]): GrantedTool[] =>
  [...tools].sort(
    (left, right) => displayRank(left) - displayRank(right) || left.label.localeCompare(right.label),
  );
